// Intent: the owner's Excel charts open badly on the phone — these are the
// phone-first replacements (Napper-style: bars with a dotted average line and
// an "Średnia: X" footer, plus a share pie). Pure react-native-svg, tappable
// bars, no chart libraries. Reused by Zarobki and Opłaty stałe.

import { useState } from 'react';
import { Pressable, View } from 'react-native';
import Svg, { Circle, Line, Path, Polyline, Rect } from 'react-native-svg';

import { AppText } from '@/components/ui/app-text';
import { colors, radius, spacing } from '@/theme/tokens';
import { formatPln } from '@/utils/format';

export interface BarDatum {
  label: string; // short x label ("sty", "lut")
  valueGr: number;
}

/**
 * Vertical bars with a dotted average line (Napper style) + "Średnia" footer.
 * onBarPress gets the index; the selected bar is highlighted.
 */
export function BarsWithAverage({
  data,
  height = 160,
  color = colors.primary,
  onBarPress,
  selectedIndex,
}: {
  data: BarDatum[];
  height?: number;
  color?: string;
  onBarPress?: (index: number) => void;
  selectedIndex?: number | null;
}) {
  const [width, setWidth] = useState(0);
  const nonZero = data.filter((d) => d.valueGr > 0);
  const avg = nonZero.length ? nonZero.reduce((s, d) => s + d.valueGr, 0) / nonZero.length : 0;
  const maxY = Math.max(...data.map((d) => d.valueGr), avg, 1) * 1.1;

  const padY = 4;
  const chartH = height - padY * 2;
  const slot = data.length > 0 ? width / data.length : 0;
  const barW = Math.max(6, Math.min(26, slot * 0.55));

  const yOf = (v: number) => padY + chartH - (v / maxY) * chartH;

  return (
    <View>
      <View style={{ height }} onLayout={(e) => setWidth(e.nativeEvent.layout.width)}>
        {width > 0 && (
          <Svg width={width} height={height}>
            {avg > 0 && (
              <Line
                x1={0}
                y1={yOf(avg)}
                x2={width}
                y2={yOf(avg)}
                stroke={colors.outlineVariant}
                strokeWidth={2}
                strokeDasharray="3 5"
              />
            )}
            {data.map((d, i) => {
              const h = (d.valueGr / maxY) * chartH;
              const x = slot * i + (slot - barW) / 2;
              const active = selectedIndex === i;
              return (
                <Rect
                  key={i}
                  x={x}
                  y={yOf(d.valueGr)}
                  width={barW}
                  height={Math.max(h, d.valueGr > 0 ? 3 : 0)}
                  rx={4}
                  fill={active ? colors.navy : color}
                  opacity={selectedIndex != null && !active ? 0.45 : 1}
                />
              );
            })}
          </Svg>
        )}
        {/* Invisible touch slots over the bars */}
        {onBarPress && width > 0 && (
          <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, flexDirection: 'row' }}>
            {data.map((_, i) => (
              <Pressable key={i} accessibilityRole="button" onPress={() => onBarPress(i)} style={{ flex: 1 }} />
            ))}
          </View>
        )}
      </View>
      <View style={{ flexDirection: 'row' }}>
        {data.map((d, i) => (
          <AppText key={i} variant="labelMd" style={{ flex: 1, textAlign: 'center' }}>
            {d.label}
          </AppText>
        ))}
      </View>
      {avg > 0 && (
        <View
          style={{
            alignSelf: 'center',
            backgroundColor: colors.card,
            borderRadius: radius.full,
            paddingHorizontal: spacing.md,
            paddingVertical: spacing.xs,
            marginTop: spacing.sm,
          }}
        >
          <AppText variant="labelMd">
            Średnia: <AppText variant="labelLg" color={colors.navy}>{formatPln(Math.round(avg))}</AppText>
          </AppText>
        </View>
      )}
    </View>
  );
}

export interface PieDatum {
  label: string;
  valueGr: number;
  color: string;
}

/** Donut share chart with a legend (who brings what into the household). */
export function SharePie({ data, size = 168 }: { data: PieDatum[]; size?: number }) {
  const total = data.reduce((s, d) => s + d.valueGr, 0);
  if (total <= 0) return null;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 6;
  const inner = r * 0.55;

  let angle = -Math.PI / 2;
  const slices = data
    .filter((d) => d.valueGr > 0)
    .map((d) => {
      const sweep = (d.valueGr / total) * Math.PI * 2;
      const a0 = angle;
      const a1 = angle + sweep;
      angle = a1;
      return { ...d, a0, a1, pct: Math.round((100 * d.valueGr) / total) };
    });

  const arcPath = (a0: number, a1: number) => {
    // Donut slice path: outer arc a0->a1, line to inner, inner arc back.
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    const xi1 = cx + inner * Math.cos(a1);
    const yi1 = cy + inner * Math.sin(a1);
    const xi0 = cx + inner * Math.cos(a0);
    const yi0 = cy + inner * Math.sin(a0);
    return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi0} ${yi0} Z`;
  };

  return (
    <View style={{ alignItems: 'center', gap: spacing.sm }}>
      <Svg width={size} height={size}>
        {slices.length === 1 ? (
          <>
            <Circle cx={cx} cy={cy} r={r} fill={slices[0].color} />
            <Circle cx={cx} cy={cy} r={inner} fill={colors.background} />
          </>
        ) : (
          slices.map((s, i) => <Path key={i} d={arcPath(s.a0, s.a1)} fill={s.color} />)
        )}
      </Svg>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: spacing.md }}>
        {slices.map((s, i) => (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
            <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: s.color }} />
            <AppText variant="labelLg">
              {s.label} <AppText variant="labelLg" color={colors.navy}>{s.pct}%</AppText>
            </AppText>
          </View>
        ))}
      </View>
    </View>
  );
}

/** Simple line chart of a value over time (fixed-costs sum growth). */
export function TrendLine({
  points,
  height = 90,
}: {
  points: { label: string; valueGr: number }[];
  height?: number;
}) {
  const [width, setWidth] = useState(0);
  if (points.length < 2) return null;
  const maxY = Math.max(...points.map((p) => p.valueGr)) * 1.1;
  const minY = Math.min(...points.map((p) => p.valueGr)) * 0.9;
  const pad = 6;
  const xOf = (i: number) => pad + (i / (points.length - 1)) * (width - pad * 2);
  const yOf = (v: number) => pad + (1 - (v - minY) / Math.max(1, maxY - minY)) * (height - pad * 2);
  const poly = points.map((p, i) => `${xOf(i)},${yOf(p.valueGr)}`).join(' ');
  return (
    <View>
      <View style={{ height }} onLayout={(e) => setWidth(e.nativeEvent.layout.width)}>
        {width > 0 && (
          <Svg width={width} height={height}>
            <Polyline points={poly} fill="none" stroke={colors.primary} strokeWidth={3} strokeLinejoin="round" />
            {points.map((p, i) => (
              <Circle key={i} cx={xOf(i)} cy={yOf(p.valueGr)} r={4} fill={colors.navy} />
            ))}
          </Svg>
        )}
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        {points.map((p, i) => (
          <AppText key={i} variant="labelMd">
            {p.label}
          </AppText>
        ))}
      </View>
    </View>
  );
}
